'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function OneRepMaxCalculator() {
  const t = useTranslations("tools.oneRepMaxCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    weightLifted: 100,
    weightUnit: 'kg',
    reps: 5,
    outputUnit: 'kg',
    formula: 'epley'
  });

  const [showSettings, setShowSettings] = useState(false);
  const [results, setResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const { weightLifted, weightUnit, reps, outputUnit, formula } = inputs;

    if (reps < 1 || reps > 10) {
      alert(t('repsWarning'));
      return;
    }

    let weightInKg = weightLifted;
    if (weightUnit === 'lb') {
      weightInKg = weightLifted / 2.20462;
    }

    let oneRepMax;

    if (formula === 'epley') {
      oneRepMax = weightInKg * (1 + reps / 30);
    } else if (formula === 'brzycki') {
      oneRepMax = weightInKg * (36 / (37 - reps));
    } else if (formula === 'lombardi') {
      oneRepMax = weightInKg * Math.pow(reps, 0.10);
    }

    let displayWeight = oneRepMax!;
    let displayUnit = 'kg';

    if (outputUnit === 'lb') {
      displayWeight = oneRepMax! * 2.20462;
      displayUnit = 'lb';
    }

    const percentages = [95, 90, 85, 80, 75, 70, 65, 60];
    const percentageResults = percentages.map(percent => ({
      percent,
      value: (displayWeight * percent / 100).toFixed(1),
      unit: displayUnit
    }));

    setResults({
      oneRepMax: displayWeight.toFixed(1),
      unit: displayUnit,
      formula,
      percentages: percentageResults
    });
  };

  const handleClear = () => {
    setInputs({
      weightLifted: 100,
      weightUnit: 'kg',
      reps: 5,
      outputUnit: 'kg',
      formula: 'epley'
    });
    setResults(null);
    setShowSettings(false);
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { margin: '0 auto', background: 'white', borderRadius: '1rem', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)', overflow: 'hidden' },
    { maxWidth: '100%' },
    { maxWidth: '600px' },
    { maxWidth: '800px' }
  );

  const toolHeaderStyle = {
    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
    color: 'white',
    padding: '2rem',
    textAlign: 'center' as const
  };

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const formStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = {
    marginBottom: '1.5rem'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 600,
    color: '#1e293b',
    fontSize: '0.95rem'
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    border: '2px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box' as const
  };

  const selectStyle = {
    ...inputStyle,
    cursor: 'pointer'
  };

  const inputRowStyle = getResponsiveStyle(
    { display: 'grid', gap: '0.75rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '2fr 1fr' },
    { gridTemplateColumns: '2fr 1fr' }
  );

  const settingsToggleStyle = {
    textAlign: 'center' as const,
    margin: '1.5rem 0'
  };

  const settingsButtonStyle = {
    background: 'none',
    border: 'none',
    color: '#2563eb',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '1rem',
    padding: '0.5rem 1rem',
    transition: 'color 0.3s ease'
  };

  const settingsContentStyle = {
    display: showSettings ? 'block' : 'none',
    padding: '1.5rem',
    background: '#f8fafc',
    borderRadius: '0.5rem',
    marginTop: '1rem'
  };

  const radioGroupStyle = {
    margin: '1rem 0'
  };

  const radioOptionStyle = {
    display: 'flex',
    alignItems: 'center',
    margin: '0.5rem 0',
    cursor: 'pointer'
  };

  const radioInputStyle = {
    width: 'auto',
    marginRight: '0.5rem',
    cursor: 'pointer'
  };

  const radioLabelStyle = {
    margin: 0,
    cursor: 'pointer',
    fontWeight: 400
  };

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      marginTop: '1rem'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '1.25rem 2.5rem', fontSize: '1.25rem' }
  );

  const clearButtonStyle = {
    width: '100%',
    padding: '0.75rem',
    background: 'white',
    color: '#64748b',
    border: '2px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginTop: '0.5rem'
  };

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderTop: '3px solid #22c55e',
      display: results ? 'block' : 'none'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: '#15803d', textAlign: 'center' as const },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const resultMainStyle = {
    background: 'white',
    padding: '1.5rem',
    borderRadius: '0.75rem',
    marginBottom: '1.5rem',
    textAlign: 'center' as const,
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
  };

  const resultMainLabelStyle = {
    fontSize: '0.9rem',
    color: '#64748b',
    marginBottom: '0.5rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em'
  };

  const resultMainValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#2563eb', margin: '0.5rem 0' },
    { fontSize: '2rem' },
    { fontSize: '2.25rem' },
    { fontSize: '2.5rem' }
  );

  const resultMainUnitStyle = {
    fontSize: '1.2rem',
    color: '#64748b'
  };

  const percentageTableStyle = {
    background: 'white',
    borderRadius: '0.75rem',
    overflow: 'hidden',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
  };

  const percentageTableH4Style = {
    margin: 0,
    padding: '1rem',
    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
    color: 'white',
    fontSize: '1.1rem',
    textAlign: 'center' as const
  };

  const percentageRowStyle = (index: number) => getResponsiveStyle(
    {
      display: 'grid',
      padding: '0.75rem 1rem',
      borderBottom: '1px solid #f1f5f9',
      alignItems: 'center',
      background: index % 2 === 0 ? '#f8fafc' : 'white'
    },
    { gridTemplateColumns: '1fr', gap: '0.5rem' },
    { gridTemplateColumns: '1fr 1fr 1fr', gap: 0 },
    { gridTemplateColumns: '1fr 1fr 1fr', gap: 0 }
  );

  const percentageLabelStyle = {
    fontWeight: 600,
    color: '#1e293b'
  };

  const percentageValueStyle = getResponsiveStyle(
    { color: '#2563eb', fontWeight: 600 },
    { textAlign: 'left' as const },
    { textAlign: 'center' as const },
    { textAlign: 'center' as const }
  );

  const percentagePurposeStyle = getResponsiveStyle(
    { color: '#64748b', fontSize: '0.85rem' },
    { textAlign: 'left' as const },
    { textAlign: 'right' as const },
    { textAlign: 'right' as const }
  );

  const infoNoteStyle = {
    marginTop: '1.5rem',
    padding: '1rem',
    background: '#fef3c7',
    borderLeft: '4px solid #f59e0b',
    borderRadius: '0.5rem',
    fontSize: '0.9rem',
    color: '#92400e'
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = {
    ...paragraphStyle,
    paddingLeft: '1.5rem'
  };

  const listItemStyle = {
    marginBottom: '0.75rem'
  };

  const strongStyle = {
    fontWeight: 600,
    color: colors.textPrimary
  };

  const linkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    borderBottom: `1px solid ${colors.primary}`,
    transition: 'color 0.3s ease'
  };

  const purposeLabels: { [key: number]: string } = {
    95: t('purposeMaxStrength'),
    90: t('purposeStrength'),
    85: t('purposeStrengthPower'),
    80: t('purposePower'),
    75: t('purposeHypertrophy'),
    70: t('purposeHypertrophy'),
    65: t('purposeEndurance'),
    60: t('purposeEndurance')
  };

  const formulaNames: { [key: string]: string } = {
    epley: t('formulaEpley'),
    brzycki: t('formulaBrzycki'),
    lombardi: t('formulaLombardi')
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>{t('toolTitle')}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="weight-lifted">{t('weightLiftedLabel')}</label>
              <div style={inputRowStyle}>
                <input
                  type="number"
                  id="weight-lifted"
                  style={inputStyle}
                  step="0.1"
                  min="0"
                  value={inputs.weightLifted}
                  onChange={(e) => setInputs({...inputs, weightLifted: parseFloat(e.target.value) || 0})}
                  required
                />
                <select
                  id="weight-unit"
                  style={selectStyle}
                  value={inputs.weightUnit}
                  onChange={(e) => setInputs({...inputs, weightUnit: e.target.value})}
                >
                  <option value="kg">{t('unitKilograms')}</option>
                  <option value="lb">{t('unitPounds')}</option>
                </select>
              </div>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="reps">{t('repsLabel')}</label>
              <input
                type="number"
                id="reps"
                style={inputStyle}
                min="1"
                max="10"
                value={inputs.reps}
                onChange={(e) => setInputs({...inputs, reps: parseInt(e.target.value) || 1})}
                required
              />
            </div>

            <div style={settingsToggleStyle}>
              <button
                type="button"
                style={settingsButtonStyle}
                onClick={() => setShowSettings(!showSettings)}
              >
                {showSettings ? t('settingsHide') : t('settingsShow')}
              </button>
            </div>

            <div style={settingsContentStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('desiredUnitLabel')}</label>
                <div style={radioGroupStyle}>
                  <div style={radioOptionStyle}>
                    <input
                      type="radio"
                      id="output-kg"
                      name="output-unit"
                      value="kg"
                      style={radioInputStyle}
                      checked={inputs.outputUnit === 'kg'}
                      onChange={(e) => setInputs({...inputs, outputUnit: e.target.value})}
                    />
                    <label style={radioLabelStyle} htmlFor="output-kg">{t('unitKilogram')}</label>
                  </div>
                  <div style={radioOptionStyle}>
                    <input
                      type="radio"
                      id="output-lb"
                      name="output-unit"
                      value="lb"
                      style={radioInputStyle}
                      checked={inputs.outputUnit === 'lb'}
                      onChange={(e) => setInputs({...inputs, outputUnit: e.target.value})}
                    />
                    <label style={radioLabelStyle} htmlFor="output-lb">{t('unitPound')}</label>
                  </div>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('formulaLabel')}</label>
                <div style={radioGroupStyle}>
                  <div style={radioOptionStyle}>
                    <input
                      type="radio"
                      id="formula-epley"
                      name="formula"
                      value="epley"
                      style={radioInputStyle}
                      checked={inputs.formula === 'epley'}
                      onChange={(e) => setInputs({...inputs, formula: e.target.value})}
                    />
                    <label style={radioLabelStyle} htmlFor="formula-epley">{t('formulaEpley')}</label>
                  </div>
                  <div style={radioOptionStyle}>
                    <input
                      type="radio"
                      id="formula-brzycki"
                      name="formula"
                      value="brzycki"
                      style={radioInputStyle}
                      checked={inputs.formula === 'brzycki'}
                      onChange={(e) => setInputs({...inputs, formula: e.target.value})}
                    />
                    <label style={radioLabelStyle} htmlFor="formula-brzycki">{t('formulaBrzycki')}</label>
                  </div>
                  <div style={radioOptionStyle}>
                    <input
                      type="radio"
                      id="formula-lombardi"
                      name="formula"
                      value="lombardi"
                      style={radioInputStyle}
                      checked={inputs.formula === 'lombardi'}
                      onChange={(e) => setInputs({...inputs, formula: e.target.value})}
                    />
                    <label style={radioLabelStyle} htmlFor="formula-lombardi">{t('formulaLombardi')}</label>
                  </div>
                </div>
              </div>
            </div>

            <button type="submit" style={calculateButtonStyle}>{t('calculateButton')}</button>
            <button type="button" style={clearButtonStyle} onClick={handleClear}>{t('clearButton')}</button>
          </form>

          <div style={resultsStyle}>
            <h3 style={resultsH3Style}>{t('resultsTitle')}</h3>

            <div style={resultMainStyle}>
              <div style={resultMainLabelStyle}>{t('estimatedLabel')}</div>
              <div style={resultMainValueStyle}>{results?.oneRepMax}</div>
              <div style={resultMainUnitStyle}>{results?.unit}</div>
            </div>

            <div style={percentageTableStyle}>
              <h4 style={percentageTableH4Style}>{t('trainingGuideTitle')}</h4>
              {results?.percentages.map((item: any, index: number) => (
                <div key={item.percent} style={percentageRowStyle(index)}>
                  <span style={percentageLabelStyle}>{item.percent}%</span>
                  <span style={percentageValueStyle}>{item.value} {item.unit}</span>
                  <span style={percentagePurposeStyle}>{purposeLabels[item.percent]}</span>
                </div>
              ))}
            </div>

            <div style={infoNoteStyle}>
              <strong>{t('noteLabel')}</strong> {t('noteText')} <span>{formulaNames[results?.formula]}</span> {t('noteFormula')}
            </div>
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step4Title')}</strong> {t('step4Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step5Title')}</strong> {t('step5Text')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('insight1')}</li>
          <li style={listItemStyle}>{t('insight2')}</li>
          <li style={listItemStyle}>{t('insight3')}</li>
          <li style={listItemStyle}>{t('insight4')}</li>
          <li style={listItemStyle}>{t('insight5')}</li>
          <li style={listItemStyle}>{t('insight6')}</li>
          <li style={listItemStyle}>{t('insight7')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem'}}>{t('formulasTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('formulaEpley')}:</strong> {t('formulaEpleyDesc')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('formulaBrzycki')}:</strong> {t('formulaBrzyckiDesc')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('formulaLombardi')}:</strong> {t('formulaLombardiDesc')}</li>
        </ul>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem', marginTop: '2rem'}}>{t('bestPracticesTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('bestPractice1')}</li>
          <li style={listItemStyle}>{t('bestPractice2')}</li>
          <li style={listItemStyle}>{t('bestPractice3')}</li>
          <li style={listItemStyle}>{t('bestPractice4')}</li>
          <li style={listItemStyle}>{t('bestPractice5')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>
        <div style={{marginBottom: '1.5rem'}}>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.5rem'}}>{t('faq1Question')}</h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>
        <div style={{marginBottom: '1.5rem'}}>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.5rem'}}>{t('faq2Question')}</h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>
        <div style={{marginBottom: '1.5rem'}}>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.5rem'}}>{t('faq3Question')}</h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>
        <div style={{marginBottom: '1.5rem'}}>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.5rem'}}>{t('faq4Question')}</h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>
        <div>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.5rem'}}>{t('faq5Question')}</h3>
          <p style={paragraphStyle}>{t('faq5Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://www.calculator.net/one-rep-max-calculator.html" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('reference1')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://theicss.org/2023/09/04/the-comprehensive-guide-to-1rm-calculators/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('reference2')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.onepeloton.com/blog/one-rep-max" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('reference3')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://pmc.ncbi.nlm.nih.gov/articles/PMC3737872/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('reference4')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.acefitness.org/fitness-certifications/ace-answers/exam-preparation-blog/2894/understanding-1-rm-and-predicted-1-rm-assessments/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t('reference5')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}